import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    TextField,
    Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import Logger from "../../services/Logger";
import ConfirmDialog from "../components/ConfirmDialog";
import Item from "../components/Item";
import { ADMIN_ADVERTISEMENT_PROMOTION_PATH } from "./advertisementRoutes";
import WeeklyEmailService from "./WeeklyEmailService";

const UNSUBSCRIBE_TOKEN = "%%WEEKLY_UNSUBSCRIBE_URL%%";

function statusLabel(status) {
    if (status === "DRAFT") return "Working";
    if (status === "CONFIRMED") return "Confirmed";
    if (status === "SENT") return "Sent";
    return status || "—";
}

function statusColor(status) {
    if (status === "DRAFT") return "warning";
    if (status === "CONFIRMED") return "info";
    if (status === "SENT") return "success";
    return "default";
}

export default function WeeklyIssuePage({ issueId, onBack }) {
    const params = useParams();
    const id = issueId || params.id;
    const navigate = useNavigate();
    const [issue, setIssue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [error, setError] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [puzzleId, setPuzzleId] = useState("");

    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError("");
        try {
            const data = await WeeklyEmailService.getIssue(id);
            setIssue(data);
        } catch (err) {
            Logger.error("Load weekly email:", err);
            setIssue(null);
            setError(typeof err === "string" ? err : err?.message || "Failed to load weekly email");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const previewHtml = useMemo(() => {
        const html = typeof issue?.html === "string" ? issue.html : "";
        return html.split(UNSUBSCRIBE_TOKEN).join("https://www.puzzleinteract.com/email/unsubscribe");
    }, [issue]);

    const goBack = () => {
        if (onBack) {
            onBack();
            return;
        }
        navigate(ADMIN_ADVERTISEMENT_PROMOTION_PATH, { state: { weeklyTab: true } });
    };

    const handleRefresh = async () => {
        if (!id || busy) return;
        setBusy("refresh");
        setError("");
        try {
            const data = await WeeklyEmailService.refreshIssue(id);
            setIssue(data);
        } catch (err) {
            Logger.error("Refresh weekly email:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to refresh weekly email");
        } finally {
            setBusy("");
        }
    };

    const handleApplyJigsaw = async () => {
        if (!id || busy) return;
        const nextId = puzzleId.trim();
        if (!nextId) {
            setError("Enter a jigsaw puzzle id or play link.");
            return;
        }
        setBusy("jigsaw");
        setError("");
        try {
            const data = await WeeklyEmailService.applyJigsaw(id, nextId);
            setIssue(data);
        } catch (err) {
            Logger.error("Apply weekly jigsaw:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to apply jigsaw image");
        } finally {
            setBusy("");
        }
    };

    const handleConfirm = async () => {
        if (!id || busy) return;
        setBusy("confirm");
        setError("");
        try {
            const data = await WeeklyEmailService.confirmIssue(id);
            setIssue(data);
        } catch (err) {
            Logger.error("Confirm weekly email:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to confirm weekly email");
        } finally {
            setBusy("");
        }
    };

    const handleReopen = async () => {
        if (!id || busy) return;
        setBusy("reopen");
        setError("");
        try {
            const data = await WeeklyEmailService.reopenIssue(id);
            setIssue(data);
        } catch (err) {
            Logger.error("Reopen weekly email:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to reopen weekly email");
        } finally {
            setBusy("");
        }
    };

    const status = issue?.status || "";
    const sendLabel = issue?.scheduledFor ? dayjs(issue.scheduledFor).format("dddd, MMM D") : "Monday";

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ textTransform: "none", mb: 2 }}>
                Weekly emails
            </Button>
            <Item>
                <Card>
                    <CardContent>
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : (
                            <>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                                    <Typography variant="h6" component="h2">
                                        {issue?.subject || "Weekly email"}
                                    </Typography>
                                    <Chip size="small" label={statusLabel(status)} color={statusColor(status)} />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {status === "SENT"
                                        ? `Sent ${issue?.sentAt ? dayjs(Number(issue.sentAt)).format("YYYY-MM-DD HH:mm") : ""}. ${issue?.sentCount || 0} delivered${issue?.failedCount ? `, ${issue.failedCount} failed` : ""}.`
                                        : status === "CONFIRMED"
                                            ? `Confirmed. The Monday scheduler sends this on ${sendLabel}.`
                                            : `Check this email. Confirm it when it looks right. It sends on ${sendLabel}.`}
                                </Typography>
                                {error ? (
                                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                                        {error}
                                    </Typography>
                                ) : null}
                                <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                                    {status === "DRAFT" ? (
                                        <>
                                            <Button
                                                variant="outlined"
                                                disabled={busy !== ""}
                                                onClick={handleRefresh}
                                                sx={{ textTransform: "none" }}
                                            >
                                                Refresh puzzles
                                            </Button>
                                            <TextField
                                                size="small"
                                                label="Jigsaw puzzle id"
                                                placeholder="Play link or puzzle id"
                                                value={puzzleId}
                                                onChange={(event) => setPuzzleId(event.target.value)}
                                                sx={{ minWidth: 280, flex: "1 1 280px" }}
                                            />
                                            <Button
                                                variant="outlined"
                                                disabled={busy !== "" || puzzleId.trim() === ""}
                                                onClick={handleApplyJigsaw}
                                                sx={{ textTransform: "none" }}
                                            >
                                                Apply
                                            </Button>
                                            <Button
                                                variant="contained"
                                                disabled={busy !== ""}
                                                onClick={() => setConfirmOpen(true)}
                                                sx={{ textTransform: "none" }}
                                            >
                                                Confirm
                                            </Button>
                                        </>
                                    ) : null}
                                    {status === "CONFIRMED" ? (
                                        <Button
                                            variant="outlined"
                                            disabled={busy !== ""}
                                            onClick={handleReopen}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Reopen
                                        </Button>
                                    ) : null}
                                </Box>
                                {previewHtml ? (
                                    <Box
                                        component="iframe"
                                        title="Weekly email"
                                        sandbox=""
                                        srcDoc={previewHtml}
                                        sx={{
                                            width: "100%",
                                            height: 920,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            bgcolor: "#fff",
                                        }}
                                    />
                                ) : null}
                            </>
                        )}
                    </CardContent>
                </Card>
            </Item>
            <ConfirmDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                title="Confirm weekly email"
                targetName={issue?.subject || ""}
                message={`Confirm this email? The Monday scheduler will send it on ${sendLabel}. It will not send before then.`}
                okText="Confirm"
                cancelText="Cancel"
                callback={handleConfirm}
            />
        </Box>
    );
}
